// Enum for application statuses
export enum ApplicationStatus {
  InProgress = "inprogress",
  Pending = "pending",
  Hold = "hold",
  Rejected = "rejected",
  Onboarded = "onboarded",
  Banned = "banned",
  Active = "active",
}

// Statuses that should redirect to application-error page
const REDIRECT_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Pending,
  ApplicationStatus.Hold,
  ApplicationStatus.Rejected,
  ApplicationStatus.Onboarded,
  ApplicationStatus.Banned,
  ApplicationStatus.Active,
];

// Helper to convert string to ApplicationStatus (case-insensitive)
export function toApplicationStatus(
  status: string | undefined
): ApplicationStatus | undefined {
  if (!status) return undefined;
  const normalized = status.toLowerCase();
  const enumValues = Object.values(ApplicationStatus);
  if (enumValues.includes(normalized as ApplicationStatus)) {
    return normalized as ApplicationStatus;
  }
  return undefined;
}

// Check if status should redirect to application-error page
export function isRedirectStatus(status: string | undefined): boolean {
  const appStatus = toApplicationStatus(status);
  return appStatus !== undefined && REDIRECT_STATUSES.includes(appStatus);
}

export interface StageRedirectParams {
  nextStage: string;
  applicationId?: string;
  applicationDate?: string;
  message?: string;
  email?: string;
}

export function buildApplicationErrorUrl(params: StageRedirectParams): string {
  const { nextStage, applicationId, applicationDate, message, email } = params;
  const searchParams = new URLSearchParams();
  
  searchParams.set("status", nextStage);
  if (applicationDate) searchParams.set("applicationDate", applicationDate);
  if (message) searchParams.set("message", message);
  if (applicationId) searchParams.set("applicationId", applicationId);
  if (email) searchParams.set("email", email);
  
  return `/application-error?${searchParams.toString()}`;
}

export function getDefaultMessage(status: ApplicationStatus | undefined): string {
  switch (status) {
    case ApplicationStatus.Pending:
      return "You are already active with another employer. Our staff needs to review your applications before you can continue.";
    case ApplicationStatus.Hold:
      return "Your application is currently on hold.";
    case ApplicationStatus.Rejected:
      return "Your application was not successful.";
    case ApplicationStatus.Onboarded:
      return "You were recently onboarded for this position.";
    case ApplicationStatus.Banned:
      return "You were recently banned for this position.";
    case ApplicationStatus.Active:
      return "Your application is currently active.";
    default:
      return "You already have a recent application for this job.";
  }
}
